export const HISTORY_INTERVAL_MS = 5 * 60 * 1000;

export const RANGE_MILLISECONDS = Object.freeze({
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
});

export const VALID_HEALTH_STATUSES = new Set([
  "healthy",
  "degraded",
  "unavailable",
  "recovering",
  "idle",
]);

const finiteCount = (value) =>
  Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;

export const normalizeHistoryPayload = (payload) => {
  if (payload?.schemaVersion !== 1 || !Array.isArray(payload.samples)) {
    throw new Error("Unsupported feed-health history payload");
  }

  const samples = payload.samples
    .map((sample) => {
      const timestamp = Date.parse(sample?.at);
      if (!Number.isFinite(timestamp) || !VALID_HEALTH_STATUSES.has(sample?.status)) {
        return null;
      }
      return {
        at: new Date(timestamp).toISOString(),
        timestamp,
        status: sample.status,
        reason: typeof sample.reason === "string" ? sample.reason : null,
        trainCountSemantics:
          sample.trainCountSemantics === "validated_vehicle_schedule_active"
            ? sample.trainCountSemantics
            : null,
        movementCountSemantics:
          sample.movementCountSemantics === "validated_vehicle_movement_eligible"
            ? sample.movementCountSemantics
            : null,
        observedTrainCount: finiteCount(sample.observedTrainCount),
        expectedTrainCount: finiteCount(sample.expectedTrainCount),
        observedTrainsMoving: finiteCount(sample.observedTrainsMoving),
        expectedTrainsMoving: finiteCount(sample.expectedTrainsMoving),
        serviceDisruption:
          sample.serviceDisruption &&
          typeof sample.serviceDisruption.active === "boolean"
            ? {
                active: sample.serviceDisruption.active,
                categories: Array.isArray(sample.serviceDisruption.categories)
                  ? sample.serviceDisruption.categories.map(String)
                  : [],
                alertCount: finiteCount(sample.serviceDisruption.alertCount),
                affectedTrains: Array.isArray(
                  sample.serviceDisruption.affectedTrains,
                )
                  ? sample.serviceDisruption.affectedTrains.map(String)
                  : [],
              }
            : null,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.timestamp - right.timestamp);

  return {
    schemaVersion: 1,
    generatedAt: payload.generatedAt || null,
    intervalSeconds: Number.isFinite(payload.intervalSeconds)
      ? payload.intervalSeconds
      : HISTORY_INTERVAL_MS / 1000,
    retentionDays: Number.isFinite(payload.retentionDays)
      ? payload.retentionDays
      : 30,
    samples,
  };
};

export const samplesForRange = (samples, range, now = new Date()) => {
  const rangeMs = RANGE_MILLISECONDS[range] || RANGE_MILLISECONDS["24h"];
  const end = now.getTime();
  const start = end - rangeMs;
  return (Array.isArray(samples) ? samples : []).filter(
    (sample) => sample.timestamp >= start && sample.timestamp <= end,
  );
};

export const calculateAvailability = (samples) => {
  const monitored = (Array.isArray(samples) ? samples : []).filter(
    (sample) => sample.status !== "idle",
  );
  if (monitored.length === 0) return null;
  const healthy = monitored.filter((sample) => sample.status === "healthy").length;
  return (healthy / monitored.length) * 100;
};

export const countIncidents = (samples) => {
  let incidents = 0;
  let previousProblem = false;
  (Array.isArray(samples) ? samples : []).forEach((sample) => {
    const problem = ["degraded", "unavailable"].includes(sample.status);
    if (problem && !previousProblem) incidents += 1;
    previousProblem = problem;
  });
  return incidents;
};

export const downsampleHistory = (samples, maximumPoints = 720) => {
  if (!Array.isArray(samples) || samples.length <= maximumPoints) {
    return samples || [];
  }
  const stride = Math.ceil(samples.length / maximumPoints);
  const selected = samples.filter(
    (sample, index) =>
      index === 0 ||
      index === samples.length - 1 ||
      index % stride === 0 ||
      sample.status !== samples[index - 1]?.status,
  );
  return selected;
};

export const buildStatusSegments = (
  samples,
  rangeStart,
  rangeEnd,
  intervalMs = HISTORY_INTERVAL_MS,
) => {
  const segments = [];
  let current = null;

  (Array.isArray(samples) ? samples : []).forEach((sample, index) => {
    const nextTimestamp = samples[index + 1]?.timestamp;
    const sampleEnd = Math.min(
      rangeEnd,
      sample.timestamp + intervalMs,
      Number.isFinite(nextTimestamp) ? nextTimestamp : rangeEnd,
    );
    const start = Math.max(rangeStart, sample.timestamp);
    if (sampleEnd <= start) return;

    if (
      current &&
      current.status === sample.status &&
      current.reason === sample.reason &&
      start - current.end <= intervalMs * 0.5
    ) {
      current.end = sampleEnd;
      return;
    }

    current = {
      start,
      end: sampleEnd,
      status: sample.status,
      reason: sample.reason,
    };
    segments.push(current);
  });

  return segments;
};

export const buildServiceDisruptionSegments = (
  samples,
  rangeStart,
  rangeEnd,
  intervalMs = HISTORY_INTERVAL_MS,
) => {
  const segments = [];
  let current = null;
  (Array.isArray(samples) ? samples : []).forEach((sample, index) => {
    if (!sample.serviceDisruption?.active) {
      current = null;
      return;
    }
    const nextTimestamp = samples[index + 1]?.timestamp;
    const start = Math.max(rangeStart, sample.timestamp);
    const end = Math.min(
      rangeEnd,
      sample.timestamp + intervalMs,
      Number.isFinite(nextTimestamp) ? nextTimestamp : rangeEnd,
    );
    const categories = [...sample.serviceDisruption.categories].sort();
    const categoryKey = categories.join(",");
    if (
      current &&
      current.categoryKey === categoryKey &&
      start - current.end <= intervalMs * 0.5
    ) {
      current.end = end;
      current.alertCount = Math.max(
        current.alertCount,
        sample.serviceDisruption.alertCount,
      );
      return;
    }
    current = {
      start,
      end,
      categories,
      categoryKey,
      alertCount: sample.serviceDisruption.alertCount,
    };
    segments.push(current);
  });
  return segments;
};

export const reasonLabel = (reason) => {
  const labels = {
    vehicle_fetch_failed: "Vehicle feed request failed",
    malformed_vehicle_activity: "Malformed vehicle data",
    empty_vehicle_activity: "No vehicles were reported",
    excessive_invalid_records: "Too many invalid vehicle records",
    low_vehicle_coverage: "Too few expected trains were reported",
    sudden_vehicle_collapse: "Reported train count dropped suddenly",
    fleet_positions_frozen: "Train locations stopped changing",
    schedule_unavailable: "Schedule data unavailable",
  };
  return labels[reason] || "Vehicle data did not pass validation";
};
